<template>
    <div>
        <DRow class="mb-3 justify-content-center">
            <DCol col="4">
                <DInput
                    id="service_fee_valid_from"
                    v-model="form.valid_from"
                    :label="__('tools.price_adjustments.table.valid_from')"
                    :text="__('tools.service_fees.form.valid_from_hint')"
                    :is-valid="isFormFieldValid('valid_from')"
                    type="date"
                    :invalid-feedback="errors.valid_from"
                />
            </DCol>
            <DCol col="4">
                <DSelect
                    id="service_fee_accounting_product"
                    v-model="form.accounting_product"
                    :label="__('tools.price_adjustments.table.accounting_product')"
                    :text="__('tools.service_fees.form.accounting_product_hint')"
                    :options="accountingProducts"
                    :is-valid="isFormFieldValid('accounting_product')"
                    :invalid-feedback="errors.accounting_product"
                />
            </DCol>
        </DRow>
        <DListGroup>
            <DListGroupItem class="d-flex justify-content-around align-items-center group-header">
                <div class="col-md-2">
                    <span class="required">{{ __('tools.service_fees.table.name') }}</span>
                    <small class="text-muted d-block fw-normal">{{ __('tools.service_fees.table.name_hint') }}</small>
                </div>
                <div class="col-md-2">
                    {{ __('tools.service_fees.table.description') }}
                    <small class="text-muted d-block fw-normal">{{ __('tools.service_fees.table.description_hint') }}</small>
                </div>
                <div class="col-md-2">
                    <span class="required">{{ __('tools.service_fees.table.net_from') }}</span>
                    <small class="text-muted d-block fw-normal">{{ __('tools.service_fees.table.net_from_hint') }}</small>
                </div>
                <div class="col-md-2">
                    <span class="required">{{ __('tools.service_fees.table.price') }}</span>
                    <small class="text-muted d-block fw-normal">{{ __('tools.service_fees.table.price_hint') }}</small>
                </div>
                <div class="col-md-1">
                    <span class="required">{{ __('tools.service_fees.table.currency') }}</span>
                    <small class="text-muted d-block fw-normal">{{ __('tools.service_fees.table.currency_hint') }}</small>
                </div>
                <div class="col-md-2">
                    <span class="required">{{ __('tools.service_fees.table.market_region') }}</span>
                    <small class="text-muted d-block fw-normal">{{ __('tools.service_fees.table.market_region_hint') }}</small>
                </div>
                <div class="col-md-1" />
            </DListGroupItem>
            <DListGroupItem
                v-for="(record, key) in recordsData"
                :key="key"
                class="d-flex justify-content-around py-3"
            >
                <div class="col-md-2 px-1">
                    <DInput
                        v-model="record.name"
                        :invalid-feedback="getError(`records.${key}.name`, key)"
                    />
                </div>
                <div class="col-md-2 px-1">
                    <DInput
                        v-model="record.description"
                        :invalid-feedback="getError(`records.${key}.description`, key)"
                    />
                </div>
                <div class="col-md-2 px-1">
                    <DInput
                        v-model="record.net_from"
                        :invalid-feedback="getError(`records.${key}.net_from`, key)"
                    />
                </div>
                <div class="col-md-2 px-1">
                    <DInput
                        v-model="record.price"
                        :invalid-feedback="getError(`records.${key}.price`, key)"
                    />
                </div>
                <div class="col-md-1 px-1">
                    <DInput
                        v-model="record.currency"
                        disabled
                    />
                </div>
                <div class="col-md-2 px-1">
                    <DMultiSelect
                        id="market-region-dropdown"
                        :options="marketRegionOptions(record)"
                        :multiple="false"
                        :search="true"
                        :selection="true"
                        :options-empty-placeholder="__('tools.service_fees.table.market_region')"
                        selection-type="text"
                        :invalid-feedback="getError(`records.${key}.market_region_id`, key)"
                        @update="(value) => updateMarketRegion(value, key)"
                    />
                </div>
                <div class="col-md-1 text-end">
                    <DButton
                        id="remove-service-fee-record"
                        icon="trash-can-outline"
                        mini
                        :disabled="isOnlyEntity(record)"
                        @click="deleteRow(record)"
                    />
                </div>
            </DListGroupItem>
        </DListGroup>
        <DFormControls
            :cancel-route="cancelRoute"
            :dirty="form.isDirty"
            @submit="submitForm"
        >
            <template #additional_button_before_submit>
                <DFab
                    :color="'secondary'"
                    icon="plus"
                    :label="__('tools.service_fees.add_record')"
                    @click="addNewRow()"
                />
            </template>
        </DFormControls>
    </div>
</template>

<script>
export default {
    name: 'ServiceFeeForm',
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        serviceFee: {
            type: Object,
            default: () => { return {} }
        },
        accountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        marketRegionsOptions: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                id: this.serviceFee?.id ?? null,
                valid_from: this.serviceFee?.validFrom ?? null,
                accounting_product: null,
                records: []
            }),
            fakeKey: typeof this.serviceFee.records !== 'undefined' ? this.serviceFee.records.length - 1 : 0,
            recordsData: this.serviceFee?.records ?? [this.getNewRow()]
        }
    },
    computed: {
        cancelRoute () {
            return this.$route('tools.service-fees.index')
        },
        errorsArray () {
            return Object.entries(this.errors).map(([key, value]) => ({ key, value }))
        }
    },
    methods: {
        submitForm () {
            if (this.recordsData.length === 0) {
                return
            }

            this.form.records = this.flattenRecords(this.recordsData)

            this.$emit('form-submitted', this.form)
        },
        flattenRecords (records) {
            const result = {}

            for (const record of records) {
                result[record.key] = JSON.parse(JSON.stringify(record))
            }

            return result
        },
        getNewRow () {
            return {
                key: 0,
                id: null,
                name: null,
                description: null,
                price: null,
                net_from: null,
                market_region_id: null,
                currency: null
            }
        },
        addNewRow () {
            const newEmptyRow = this.getNewRow()
            newEmptyRow.key = ++this.fakeKey
            this.recordsData.push(newEmptyRow)
        },
        getError (key, index) {
            const error = this.errorsArray.find((log) => {
                return log.key === key
            })
            return typeof error !== 'undefined' ? error.value.replace('records.' + index + '.', '') : null
        },
        deleteRow (item) {
            for (const record of this.recordsData) {
                if (record.key === item.key) {
                    this.recordsData.splice(this.recordsData.indexOf(item), 1)

                    return
                }
            }
        },
        isOnlyEntity (item) {
            return this.recordsData.length === 1 && this.recordsData[0].key === item.key
        },
        updateMarketRegion (value, index) {
            const marketRegionId = value[0].value

            this.recordsData[index].market_region_id = marketRegionId
            this.recordsData[index].currency = this.findCurrency(marketRegionId)
        },
        marketRegionOptions (record) {
            if (record.market_region_id) {
                this.recordsData[record.key].currency = this.findCurrency(record.market_region_id)
            }

            return this.marketRegionsOptions.map((value) => {
                return {
                    value,
                    text: `${value.label}`,
                    selected: value.value === record.market_region_id
                }
            })
        },
        findCurrency (marketRegionId) {
            const marketRegion = this.$page.props.marketRegions?.[marketRegionId]

            return marketRegion?.currency_code ?? 'N/A'
        },
    }
}
</script>

<style lang="scss">
.group-header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        font-weight: bold;
    }
}
.width-date {
    width: 40%
}
.required::after {
    color: var(--colorDanger);
}
</style>
